import { FormControl, FormErrorMessage, Input, Select } from "@chakra-ui/react";
import {
	Control,
	useForm,
	UseFormRegister,
	useController,
} from "react-hook-form";
import CertificateIcon from "../assets/certificate-icon.png";
import { Gender } from "src/types";
import { JobAdTagsQuery } from "@/__generated__/graphql";
import {
	Form,
	FormDescription,
	FormField,
	FormItem,
	FormMessage,
} from "./ui/form";
import { PlusIcon, XIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import {
	Command,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandEmpty,
} from "./ui/command";
import { Fragment, useState, useEffect } from "react";
import { Badge } from "./ui/badge";
import { logEvent } from "src/utlis/analytics";

type PersonalInfo = {
	firstName: string;
	lastName: string;
	dateOfBirth: string;
	gender: Gender;
	jobTitle: string;
	tags?: string[];
	userAddedTags?: string[];
};

type PersonalInformationFormProps = {
	tags: JobAdTagsQuery["jobAdTags"];
	control: Control<PersonalInfo, any>;
	registerPersonalInfo: UseFormRegister<PersonalInfo>;
	touchedFields: Partial<
		Readonly<{
			firstName?: boolean | undefined;
			lastName?: boolean | undefined;
			dateOfBirth?: boolean | undefined;
			gender?: boolean | undefined;
			jobTitle?: boolean | undefined;
			tags?: boolean[] | undefined;
			userAddedTags?: boolean[] | undefined;
		}>
	>;
	formData: PersonalInfo;
};

export default function PersonalInformationForm({
	tags,
	control,
	registerPersonalInfo,
	touchedFields,
	formData,
}: PersonalInformationFormProps) {
	// Set up controller for parent form userAddedTags field
	const { field: userAddedTagsField } = useController({
		name: "userAddedTags",
		control,
		defaultValue: formData.userAddedTags || [],
	});

	const form = useForm<PersonalInfo>({
		defaultValues: {
			...formData,
			userAddedTags: formData.userAddedTags || [],
		},
	});

	const [searchTerm, setSearchTerm] = useState("");

	const userAddedTags = form.watch("userAddedTags") || [];

	// Sync local form with parent form when userAddedTags change
	useEffect(() => {
		const subscription = form.watch((value) => {
			if (value.userAddedTags) {
				// Update parent form's userAddedTags directly
				userAddedTagsField.onChange(value.userAddedTags);
			}
		});

		return () => subscription.unsubscribe();
	}, [form, userAddedTagsField]);

	const tagGroups = tags.reduce((acc, tag) => {
		if (!acc[tag.group]) {
			acc[tag.group] = [];
		}

		acc[tag.group].push(tag);

		return acc;
	}, {} as Record<string, JobAdTagsQuery["jobAdTags"]>);

	const addCustomTag = (tagName: string) => {
		if (tagName.trim()) {
			// Add to user added tags using current form state
			const updatedTags = [...userAddedTags, tagName.trim()];

			// Update both local and parent form
			form.setValue("userAddedTags", updatedTags, {
				shouldDirty: true,
				shouldTouch: true,
				shouldValidate: true,
			});

			logEvent("pro/app/custom_profession_tag_added", {
				tag: tagName,
			});

			setSearchTerm("");
		}
	};

	// Check if we have any tags (either from formData or userAddedTags)
	const hasTags = Boolean(
		(formData.tags && formData.tags.length > 0) ||
			(userAddedTags && userAddedTags.length > 0)
	);

	return (
		<div className="overflow-y-scroll grid grid-cols-1 lg:grid-cols-2">
			<div className="hidden lg:block py-8">
				<div className="mb-8 bg-[#EDEDED] w-[72px] h-[72px] rounded-full flex items-center justify-center p-6">
					<img src={CertificateIcon} alt="Certificate Icon" />
				</div>
				<div className="flex flex-col w-[70%]">
					<h2 className="mb-2 text-xl lg:text-2xl font-medium text-[#121212] font-[AeonikPro]">
						Personal Information
					</h2>
					<p className="text-lg lg:text-xl text-[#464646]">
						Provide your personal details in the required fields.
					</p>
				</div>
			</div>
			<div className="border-l-[0] lg:border-l-[1px] border-[#E7E7E7] lg:pl-12 py-8 flex flex-col gap-6">
				<Form {...form}>
					<FormControl
						isInvalid={
							!!(
								touchedFields.firstName &&
								(!formData.firstName || formData.firstName.includes(" "))
							)
						}
					>
						<div className="flex flex-col gap-4">
							<label
								htmlFor="firstName"
								className="text-base lg:text-xl text-[#212121] font-medium"
							>
								First Name
							</label>
							<div className="flex flex-col gap-1">
								<Input
									{...registerPersonalInfo("firstName")}
									type="text"
									id="firstName"
									placeholder="Enter your first name"
									rounded={0}
									py={{
										base: 5,
										lg: 8,
									}}
									px={{
										base: 3,
										lg: 5,
									}}
									border={"1px solid #D7D7D7"}
								/>
								<FormErrorMessage>
									{formData.firstName
										? "First name cannot contain spaces"
										: "Please enter your first name"}
								</FormErrorMessage>
							</div>
						</div>
					</FormControl>
					<FormControl
						isInvalid={
							!!(
								touchedFields.lastName &&
								(!formData.lastName || formData.lastName.includes(" "))
							)
						}
					>
						<div className="flex flex-col gap-4">
							<label
								htmlFor="lastName"
								className="text-base lg:text-xl text-[#212121] font-medium"
							>
								Last Name
							</label>
							<div className="flex flex-col gap-1">
								<Input
									{...registerPersonalInfo("lastName")}
									type="text"
									id="lastName"
									placeholder="Enter your last name"
									rounded={0}
									py={{
										base: 5,
										lg: 8,
									}}
									px={{
										base: 3,
										lg: 5,
									}}
									border={"1px solid #D7D7D7"}
								/>
								<FormErrorMessage>
									{formData.lastName
										? "Last name cannot contain spaces"
										: "Please enter your last name"}
								</FormErrorMessage>
							</div>
						</div>
					</FormControl>
					<FormControl
						isInvalid={!!(touchedFields.dateOfBirth && !formData.dateOfBirth)}
					>
						<div className="flex flex-col gap-4">
							<label
								htmlFor="dateOfBirth"
								className="text-base lg:text-xl text-[#212121] font-medium"
							>
								Date of Birth
							</label>
							<div className="flex flex-col gap-1">
								<Input
									{...registerPersonalInfo("dateOfBirth")}
									type="date"
									id="dateOfBirth"
									max={new Date().toISOString().split("T")[0]}
									placeholder="Enter your date of birth"
									rounded={0}
									py={{
										base: 5,
										lg: 8,
									}}
									px={{
										base: 3,
										lg: 5,
									}}
									border={"1px solid #D7D7D7"}
								/>
								<FormErrorMessage>
									Please enter your date of birth
								</FormErrorMessage>
							</div>
						</div>
					</FormControl>
					<FormControl isInvalid={!!(touchedFields.gender && !formData.gender)}>
						<div className="flex flex-col gap-4">
							<label
								htmlFor="gender"
								className="text-base lg:text-xl text-[#212121] font-medium"
							>
								Gender
							</label>
							<div className="flex flex-col gap-1">
								<Select
									{...registerPersonalInfo("gender")}
									rounded={0}
									size={{
										base: "md",
										lg: "lg",
									}}
									h={{
										base: 10,
										lg: 16,
									}}
								>
									<option value={""}>Select Option</option>
									<option value={Gender.MALE}>Male</option>
									<option value={Gender.FEMALE}>Female</option>
								</Select>
								<FormErrorMessage>Please select your gender</FormErrorMessage>
							</div>
						</div>
					</FormControl>
					<FormControl
						isInvalid={!!(touchedFields.jobTitle && !formData.jobTitle)}
					>
						<div className="flex flex-col gap-4">
							<label
								htmlFor="jobTitle"
								className="text-base lg:text-xl text-[#212121] font-medium"
							>
								Profession
							</label>
							<div className="flex flex-col gap-1">
								<Input
									{...registerPersonalInfo("jobTitle")}
									type="text"
									id="jobTitle"
									placeholder="Enter your profession"
									rounded={0}
									py={{
										base: 5,
										lg: 8,
									}}
									px={{
										base: 3,
										lg: 5,
									}}
									border={"1px solid #D7D7D7"}
								/>
								<FormErrorMessage>
									Please input your profession
								</FormErrorMessage>
							</div>
						</div>
					</FormControl>
					<FormControl isInvalid={!!(touchedFields.tags && !hasTags)}>
						<div className="flex flex-col gap-4">
							<FormField
								control={control}
								name="tags"
								render={({ field }) => (
									<FormItem>
										<label
											htmlFor="tags"
											className="text-base lg:text-xl text-[#212121] font-medium"
										>
											Profession tags
										</label>
										<div className="flex flex-col gap-3">
											<div className="flex flex-wrap gap-2">
												{field.value?.map((tagId) => {
													const tag = tags.find((t) => t.id === tagId);
													return (
														<Badge
															key={tagId}
															className="px-3 py-1 text-sm rounded-full"
														>
															{tag?.name}
															<button
																type="button"
																className="ml-2 rounded-full outline-none hover:bg-accent"
																onClick={() => {
																	field.onChange(
																		field.value?.filter((id) => id !== tagId)
																	);
																}}
															>
																<XIcon className="w-3 h-3" />
															</button>
														</Badge>
													);
												})}
												{userAddedTags.map((tagName) => (
													<Badge
														key={`custom-${tagName}`}
														className="px-3 py-1 text-sm rounded-full"
													>
														{tagName}
														<button
															type="button"
															className="ml-2 rounded-full outline-none hover:bg-accent"
															onClick={() => {
																const updatedTags = userAddedTags.filter(
																	(t) => t !== tagName
																);
																// Update both local and parent form
																form.setValue("userAddedTags", updatedTags, {
																	shouldDirty: true,
																	shouldTouch: true,
																	shouldValidate: true,
																});
															}}
														>
															<XIcon className="w-3 h-3" />
														</button>
													</Badge>
												))}
												<Popover>
													<PopoverTrigger asChild>
														<Button
															type="button"
															variant="secondary"
															size="sm"
															className="h-8 rounded-full px-3"
															onClick={() => {
																logEvent("pro/app/add_profession_tag_clicked");
															}}
														>
															<PlusIcon className="w-4 h-4 mr-1" />
															Add Profession Tag
														</Button>
													</PopoverTrigger>
													<PopoverContent className="p-0" align="start">
														<Command
															filter={(value, search, keywords) => {
																// Skip filtering if search is empty
																if (!search) return 1;

																const searchLower = search.toLowerCase();

																// check if the value starts with the search term (highest priority)
																if (value.toLowerCase().startsWith(searchLower))
																	return 1;

																// check if any keyword starts with the search term
																if (
																	keywords?.some((keyword) =>
																		keyword
																			.toLowerCase()
																			.startsWith(searchLower)
																	)
																)
																	return 0.8;

																// check if value contains the search term
																if (value.toLowerCase().includes(searchLower))
																	return 0.6;

																// finally check if any keyword contains the search term
																if (
																	keywords?.some((keyword) =>
																		keyword.toLowerCase().includes(searchLower)
																	)
																)
																	return 0.4;

																// No match
																return 0;
															}}
														>
															<CommandInput
																placeholder="Search tags..."
																onValueChange={setSearchTerm}
															/>
															<CommandList>
																{Object.entries(tagGroups).map(
																	([group, tags], index) => (
																		<Fragment key={group}>
																			<CommandGroup key={group} heading={group}>
																				{tags.map((tag) => (
																					<CommandItem
																						key={tag.id}
																						value={tag.name}
																						keywords={[tag.group]}
																						onSelect={() => {
																							if (
																								!field.value?.includes(tag.id)
																							) {
																								field.onChange([
																									...(field.value || []),
																									tag.id,
																								]);
																								logEvent(
																									"pro/app/profession_tag_added",
																									{
																										tag: tag.name,
																									}
																								);
																							}
																						}}
																						className="cursor-pointer hover:bg-accent"
																					>
																						{tag.name}
																					</CommandItem>
																				))}
																			</CommandGroup>
																			{index <
																				Object.keys(tagGroups).length - 1 && (
																				<CommandSeparator />
																			)}
																		</Fragment>
																	)
																)}
																<CommandEmpty>
																	{searchTerm.trim() && (
																		<div
																			className="my-1 mx-1 flex gap-2 cursor-pointer hover:bg-accent rounded-sm px-2 py-1.5"
																			onClick={() => addCustomTag(searchTerm)}
																		>
																			{searchTerm}
																		</div>
																	)}
																</CommandEmpty>
															</CommandList>
														</Command>
													</PopoverContent>
												</Popover>
											</div>
										</div>
										<FormMessage />
										<FormDescription>
											Add one or more tags you want to be associated with
										</FormDescription>
									</FormItem>
								)}
							/>
						</div>
					</FormControl>
				</Form>
			</div>
		</div>
	);
}
